import { OidcClient, SigninResponse, SigninState, WebStorageStateStore, type UserProfile } from "oidc-client-ts";
import { createLocalJWKSet, decodeJwt, jwtVerify, type JSONWebKeySet } from "jose";
import type {
  IOidcConfiguration,
  IOidcSession,
  IOidcTransactionContext,
  IOidcCompletedCallback,
} from "~/utils/interfaces/oidc-interfaces";
import {
  AUTH_REQUIRED_MESSAGE,
  OIDC_SCOPE,
  OIDC_UNAVAILABLE_MESSAGE,
  OidcDiscoveryUnavailableError,
} from "./oidc-contract";
import { OidcSessionCoordinator, type SessionLock } from "./oidc-session";
import { withTimezoneHeader } from "./timezone-header";

import { isAuthLoggingOut } from "./lifecycle";
import { FetchCredentialsEnum, FetchRedirectEnum } from "~/utils/enums/common.enums";

const TRANSACTION_MAX_AGE_SECONDS = 600;
const CLOCK_TOLERANCE_SECONDS = 30;
export const OIDC_PENDING_KEY = "teams.oidc.pending";
const OIDC_STATE_PREFIX = "teams.oidc.transaction.";
const OIDC_COMPLETED_KEY = "teams.oidc.completed-callback";

/** Protocol mechanics belong to oidc-client-ts; persistence is committed only after validation. */
export class TeamsOidcClient {
  readonly sessions: OidcSessionCoordinator;
  private readonly client: OidcClient;

  constructor(
    readonly config: IOidcConfiguration,
    private readonly persistent: Storage,
    private readonly transactions: Storage,
    private readonly lock: SessionLock,
    readonly embedded = false,
  ) {
    this.sessions = new OidcSessionCoordinator(persistent, lock, undefined, embedded);
    this.client = new OidcClient({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      post_logout_redirect_uri: config.postLogoutRedirectUri,
      response_type: "code",
      scope: OIDC_SCOPE,
      resource: config.resource,
      extraTokenParams: { resource: config.resource },
      loadUserInfo: false,
      disablePKCE: false,
      fetchRequestCredentials: FetchCredentialsEnum.OMIT,
      stateStore: new WebStorageStateStore({ store: transactions, prefix: OIDC_STATE_PREFIX }),
      staleStateAgeInSeconds: TRANSACTION_MAX_AGE_SECONDS,
      requestTimeoutInSeconds: 15,
    });
  }

  private async metadata() {
    const metadata = await this.client.metadataService.getMetadata();
    if (metadata.issuer !== this.config.authority) throw new Error(OIDC_UNAVAILABLE_MESSAGE);
    for (const endpoint of [
      metadata.authorization_endpoint,
      metadata.token_endpoint,
      metadata.jwks_uri,
      metadata.end_session_endpoint,
      metadata.revocation_endpoint,
    ]) {
      if (!endpoint) continue;
      const url = new URL(endpoint);
      if (url.origin !== new URL(this.config.authority).origin || url.username || url.password || url.hash)
        throw new Error(OIDC_UNAVAILABLE_MESSAGE);
    }
    if (!metadata.authorization_endpoint || !metadata.token_endpoint || !metadata.jwks_uri)
      throw new Error(OIDC_UNAVAILABLE_MESSAGE);
    return metadata;
  }

  pending(): IOidcTransactionContext | undefined {
    try {
      return JSON.parse(this.transactions.getItem(OIDC_PENDING_KEY) ?? "null")?.context;
    } catch {
      return undefined;
    }
  }

  /** API-only launch: the code is bound to this iframe's in-memory PKCE proof. */
  async loginEmbedded(
    authorize: (proof: { codeChallenge: string; nonce: string }) => Promise<string>,
  ): Promise<IOidcSession> {
    if (!this.embedded || isAuthLoggingOut()) throw new Error(AUTH_REQUIRED_MESSAGE);
    const generation = this.sessions.clear();
    const nonce = crypto.randomUUID();
    const proof = await SigninState.create({
      authority: this.config.authority,
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: OIDC_SCOPE,
      code_verifier: true,
      nonce,
    });
    // Redeem Docebo's 30-second launch before doing discovery or loading the profile.
    const code = await authorize({ codeChallenge: proof.code_challenge!, nonce });
    if (!code || isAuthLoggingOut() || this.sessions.record()?.generation !== generation)
      throw new Error(AUTH_REQUIRED_MESSAGE);
    const metadata = await this.metadata();
    const tokenResponse = await fetch(metadata.token_endpoint!, {
      method: "POST",
      credentials: FetchCredentialsEnum.OMIT,
      redirect: FetchRedirectEnum.ERROR,
      signal: AbortSignal.timeout(15_000),
      headers: withTimezoneHeader({ "Content-Type": "application/x-www-form-urlencoded" }),
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: this.config.clientId,
        code,
        redirect_uri: this.config.redirectUri,
        code_verifier: proof.code_verifier!,
        resource: this.config.resource,
      }),
    });
    if (!tokenResponse.ok) throw new Error(AUTH_REQUIRED_MESSAGE);
    const data = await tokenResponse.json();
    if (typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in) || data.expires_in <= 0)
      throw new Error(AUTH_REQUIRED_MESSAGE);
    // This is a token response, not a fabricated browser callback.
    const response = new SigninResponse(new URLSearchParams());
    response.access_token = data.access_token;
    response.refresh_token = data.refresh_token;
    response.id_token = data.id_token;
    response.token_type = data.token_type;
    response.expires_in = data.expires_in;
    const identity = await this.validateIdentity(response.id_token ?? "", nonce);
    if (isAuthLoggingOut() || this.sessions.record()?.generation !== generation) throw new Error(AUTH_REQUIRED_MESSAGE);
    const session = this.session(response, identity.sub!);
    this.sessions.establish(session);
    return session;
  }

  /** The lock waits for an already-started rotation, so revocation uses its latest token. */
  async revokeEmbedded(): Promise<void> {
    if (!this.embedded) throw new Error(AUTH_REQUIRED_MESSAGE);
    await this.lock(async () => {
      try {
        const record = this.sessions.record();
        const session = record?.session;
        // A lost rotation response leaves the latest token unknown. Do not report a
        // successful sign-out after revoking a credential that may already be spent.
        if (!session || record.renewing) throw new Error(AUTH_REQUIRED_MESSAGE);
        const metadata = await this.metadata();
        if (!metadata.revocation_endpoint) throw new Error(OIDC_UNAVAILABLE_MESSAGE);
        const response = await fetch(metadata.revocation_endpoint, {
          method: "POST",
          credentials: FetchCredentialsEnum.OMIT,
          redirect: FetchRedirectEnum.ERROR,
          signal: AbortSignal.timeout(15_000),
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            client_id: this.config.clientId,
            token: session.refresh_token,
            token_type_hint: "refresh_token",
          }),
        });
        if (!response.ok) throw new Error(AUTH_REQUIRED_MESSAGE);
      } finally {
        this.sessions.clear();
      }
    });
  }

  async authorize(context: Omit<IOidcTransactionContext, "generation">, freshLogin = false): Promise<string> {
    if (this.embedded || isAuthLoggingOut()) throw new Error(AUTH_REQUIRED_MESSAGE);
    if (!this.sessions.record()) this.sessions.clear();
    const generation = this.sessions.record()!.generation;
    this.transactions.removeItem(OIDC_COMPLETED_KEY);
    await this.metadata();
    await this.client.clearStaleState();
    if (isAuthLoggingOut() || this.sessions.record()?.generation !== generation) throw new Error(AUTH_REQUIRED_MESSAGE);
    const bound: IOidcTransactionContext = { ...context, generation: this.sessions.record()!.generation };
    const request = await this.client.createSigninRequest({
      state: bound,
      nonce: crypto.randomUUID(),
      login_hint: context.email,
      ...(freshLogin ? { prompt: "login" } : {}),
    });
    if (isAuthLoggingOut() || this.sessions.record()?.generation !== generation) throw new Error(AUTH_REQUIRED_MESSAGE);
    this.transactions.setItem(OIDC_PENDING_KEY, JSON.stringify({ stateId: request.state.id, context: bound }));
    return request.url;
  }

  private async validateIdentity(idToken: string, nonce?: string) {
    const metadata = await this.metadata();
    const response = await fetch(metadata.jwks_uri!, {
      credentials: FetchCredentialsEnum.OMIT,
      signal: AbortSignal.timeout(15_000),
    });
    if (!response.ok) throw new Error(AUTH_REQUIRED_MESSAGE);
    const keys = createLocalJWKSet((await response.json()) as JSONWebKeySet);
    const { payload } = await jwtVerify(idToken, keys, {
      issuer: this.config.authority,
      audience: this.config.clientId,
      algorithms: ["RS256", "RS384", "RS512", "PS256", "PS384", "PS512", "ES256", "ES384", "ES512", "EdDSA"],
      requiredClaims: ["iss", "aud", "exp", "iat", "sub"],
      clockTolerance: CLOCK_TOLERANCE_SECONDS,
    });
    if (
      !payload.sub ||
      (nonce && payload.nonce !== nonce) ||
      (payload.azp !== undefined && payload.azp !== this.config.clientId) ||
      (Array.isArray(payload.aud) && payload.aud.length > 1 && payload.azp !== this.config.clientId) ||
      payload.iat! > Date.now() / 1000 + CLOCK_TOLERANCE_SECONDS
    )
      throw new Error(AUTH_REQUIRED_MESSAGE);
    return payload;
  }

  private session(response: SigninResponse, subject: string): IOidcSession {
    if (
      !response.access_token ||
      !response.refresh_token ||
      !response.id_token ||
      !response.expires_at ||
      response.token_type.toLowerCase() !== "bearer" ||
      response.expires_at <= Date.now() / 1000
    )
      throw new Error(AUTH_REQUIRED_MESSAGE);
    return {
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      id_token: response.id_token,
      expires_at: response.expires_at,
      subject,
    };
  }

  async callback(value: string): Promise<{ session: IOidcSession; context: IOidcTransactionContext }> {
    return this.lock(async () => {
      await this.metadata();
      const url = new URL(value);
      if (`${url.origin}${url.pathname}` !== this.config.redirectUri) throw new Error(AUTH_REQUIRED_MESSAGE);
      // A reload loses the scrubbed one-use response URL. Resume only our own already-validated
      // exchange, in this tab and session; an error or a repeated code must still be rejected.
      if (!url.search && !url.hash) {
        const completed = JSON.parse(
          this.transactions.getItem(OIDC_COMPLETED_KEY) ?? "null",
        ) as IOidcCompletedCallback | null;
        const current = this.sessions.record();
        if (
          !completed?.context ||
          !current?.session ||
          completed.generation !== current.generation ||
          !Number.isFinite(completed.completedAt) ||
          completed.completedAt < Date.now() / 1000 - TRANSACTION_MAX_AGE_SECONDS ||
          current.session.expires_at <= Date.now() / 1000
        )
          throw new Error(AUTH_REQUIRED_MESSAGE);
        return { session: current.session, context: completed.context };
      }
      if (
        url.searchParams.getAll("state").length !== 1 ||
        !url.searchParams.get("state") ||
        url.searchParams.getAll("code").length !== 1 ||
        !url.searchParams.get("code") ||
        url.searchParams.getAll("iss").length > 1 ||
        (url.searchParams.has("iss") && url.searchParams.get("iss") !== this.config.authority)
      )
        throw new Error(AUTH_REQUIRED_MESSAGE);
      const { state } = await this.client.readSigninResponseState(value);
      const context = state.data as IOidcTransactionContext;
      const pending = JSON.parse(this.transactions.getItem(OIDC_PENDING_KEY) ?? "null");
      if (
        !pending ||
        pending.stateId !== state.id ||
        !state.nonce ||
        !state.code_verifier ||
        state.created < Date.now() / 1000 - TRANSACTION_MAX_AGE_SECONDS ||
        !context?.generation ||
        this.sessions.record()?.generation !== context.generation
      )
        throw new Error(AUTH_REQUIRED_MESSAGE);
      const response = await this.client.processSigninResponse(value, withTimezoneHeader());
      const identity = await this.validateIdentity(response.id_token ?? "", state.nonce);
      if (isAuthLoggingOut() || this.sessions.record()?.generation !== context.generation)
        throw new Error(AUTH_REQUIRED_MESSAGE);
      const session = this.session(response, identity.sub!);
      this.sessions.establish(session);
      this.transactions.setItem(
        OIDC_COMPLETED_KEY,
        JSON.stringify({
          generation: this.sessions.record()!.generation,
          completedAt: Date.now() / 1000,
          context,
        } satisfies IOidcCompletedCallback),
      );
      this.transactions.removeItem(OIDC_PENDING_KEY);
      return { session, context };
    });
  }

  completeCallback(generation: string): void {
    const completed = JSON.parse(
      this.transactions.getItem(OIDC_COMPLETED_KEY) ?? "null",
    ) as IOidcCompletedCallback | null;
    if (completed?.generation === generation) this.transactions.removeItem(OIDC_COMPLETED_KEY);
  }

  async renew(force = false): Promise<IOidcSession> {
    if (isAuthLoggingOut()) throw new Error(AUTH_REQUIRED_MESSAGE);
    return this.sessions.renew(
      async (current) => {
        const response = await this.client.useRefreshToken({
          state: {
            refresh_token: current.refresh_token,
            id_token: current.id_token,
            session_state: null,
            scope: OIDC_SCOPE,
            profile: decodeJwt(current.id_token) as UserProfile,
          },
          resource: this.config.resource,
          extraTokenParams: { resource: this.config.resource },
          extraHeaders: withTimezoneHeader(),
          timeoutInSeconds: 15,
        });
        if (!response.refresh_token || response.refresh_token === current.refresh_token)
          throw new Error(AUTH_REQUIRED_MESSAGE);
        if (response.id_token && response.id_token !== current.id_token) {
          const identity = await this.validateIdentity(response.id_token);
          if (identity.sub !== current.subject) throw new Error(AUTH_REQUIRED_MESSAGE);
        }
        return this.session(response, current.subject);
      },
      force,
      async () => {
        try {
          await this.metadata();
        } catch {
          throw new OidcDiscoveryUnavailableError();
        }
      },
    );
  }

  async signout(idToken: string | undefined, logoutGeneration: string): Promise<string> {
    // Prepare while the original credentials still exist. The caller clears them using the
    // reserved logout generation before navigation, without deleting this SDK transaction.
    const originalGeneration = this.sessions.record()?.generation;
    const matches = () => this.sessions.record()?.generation === originalGeneration;
    if (!logoutGeneration || !matches()) throw new Error(AUTH_REQUIRED_MESSAGE);
    const metadata = await this.metadata();
    if (!metadata.end_session_endpoint) throw new Error(OIDC_UNAVAILABLE_MESSAGE);
    if (!matches()) throw new Error(AUTH_REQUIRED_MESSAGE);
    const request = await this.client.createSignoutRequest({
      ...(idToken ? { id_token_hint: idToken } : { client_id: this.config.clientId }),
      post_logout_redirect_uri: this.config.postLogoutRedirectUri,
      state: { generation: logoutGeneration },
    });
    if (!matches()) throw new Error(AUTH_REQUIRED_MESSAGE);
    return request.url;
  }

  async logoutCallback(url: string): Promise<void> {
    const parsed = new URL(url);
    if (
      `${parsed.origin}${parsed.pathname}` !== this.config.postLogoutRedirectUri ||
      parsed.searchParams.getAll("state").length !== 1 ||
      !parsed.searchParams.get("state")
    )
      throw new Error(AUTH_REQUIRED_MESSAGE);
    const { state: transaction } = await this.client.readSignoutResponseState(url);
    if (!transaction || transaction.created < Date.now() / 1000 - TRANSACTION_MAX_AGE_SECONDS)
      throw new Error(AUTH_REQUIRED_MESSAGE);
    const result = await this.client.processSignoutResponse(url);
    const state = result.userState as { generation?: string } | undefined;
    const current = this.sessions.record();
    if (!state?.generation || state.generation !== current?.generation || current.session)
      throw new Error(AUTH_REQUIRED_MESSAGE);
  }

  clearTransactions(): void {
    const keys = Array.from({ length: this.transactions.length }, (_, index) => this.transactions.key(index));
    for (const key of keys) if (key?.startsWith(OIDC_STATE_PREFIX)) this.transactions.removeItem(key);
    this.transactions.removeItem(OIDC_PENDING_KEY);
    this.transactions.removeItem(OIDC_COMPLETED_KEY);
  }
}
